<!--操作按钮容器-->
<template>
  <div class="cb-cont">
    <div v-if="$slots.left" class="left">
      <el-menu
        class="cmdbar"
        mode="horizontal"
        :ellipsis="false"
        @select="handleSelect"
      >
        <slot name="left" />
      </el-menu>
    </div>

    <el-menu
      v-if="defaultSlot.length"
      ref="cmdbarRef"
      class="cmdbar"
      mode="horizontal"
      :ellipsis="false"
      @select="handleSelect"
    >
      <template v-for="item of defaultSlot">
        <component
          :is="item.comp"
          v-if="item.show"
          ref="menusRef"
          :key="item.index"
          :style="{
            '--cmd-name': item.index,
            visibility: hiddenMenuMap[item.index] ? 'hidden' : 'visible'
          }"
          class="cb-menu"
        />
      </template>
    </el-menu>
    <div v-else class="cmdbar" />

    <el-menu
      v-if="Object.keys(hiddenMenuMap).length"
      ref="moreMenuRef"
      class="more-menu"
      mode="horizontal"
      :ellipsis="false"
      @select="handleSelect"
    >
      <el-sub-menu
        index="@more-op"
        class="more-op"
        popper-class="st-sm-popper"
        @mouseenter="onMouseEnter"
      >
        <template #title>更多操作</template>

        <template v-for="item of defaultSlot">
          <component
            :is="hiddenMenuMap[item.index]"
            v-if="hiddenMenuMap[item.index]"
            :key="'@' + item.index"
          />
        </template>
      </el-sub-menu>
    </el-menu>

    <div class="cmd-space" />

    <div v-if="$slots.right" class="right">
      <el-menu
        class="cmdbar"
        mode="horizontal"
        :ellipsis="false"
        @select="handleSelect"
      >
        <slot name="right" />
      </el-menu>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * ### 操作按钮容器组件
 * 有left/right插槽 且插槽内容不计算入“更多操作”
 * 默认插槽子组件为 cmd-menu，虽支持自定义组件，但有一些问题
 * <warning> 对于自定义组件require权限属性会失效 需自己使用v-if调用 easy.canAccess
 * <error> 如果自定义组件包含子组件 权限判断会判断为无权限 此时需将自定义组件再封装一层使其无子组件
 */
import {
  h,
  ref,
  computed,
  reactive,
  watch,
  useSlots,
  VNode,
  onBeforeUnmount
} from 'vue';
import { useElementBounding } from '@vueuse/core';
import {
  getLayout,
  saveLayout,
  mergeLayout,
  deleteLayout
} from '../../core/LayoutPersistent';

import { useShareData, shareData } from '../../core/SharedData';

import { arrayMove } from '../../core/Common';
import type { ITableCmdHandles } from '../../core/TableUtilInternal';
import { TableCtx } from '../types';
import { canAccess } from '../../core/CmdRightsControl';
import CmdMenu from './CmdMenu.vue';
import CmdMenuItem from './CmdMenuItem.vue';
import CmdSubMenu from './CmdSubMenu.vue';
import { ElMenuItem, ElSubMenu } from 'element-plus';
import { beaconHandle } from '../../core/beacon';
import _ from 'lodash-es';

const props = withDefaults(
  defineProps<{
    /**
     * ### 操作按钮句柄函数对象
     */
    handles?: ITableCmdHandles;
    /**
     * ### "更多操作" 右侧空白空间
     */
    reserveSpace?: string;
  }>(),
  {
    handles: undefined,
    reserveSpace: '5%'
  }
);

// 提取上下文
const name = useShareData('table', 'name');
const tableCtx = useShareData('table', 'ctx') as TableCtx;
props.handles?.init(tableCtx);

// 注册方法，提供给contextmenu调用
const tableCmd = useShareData('table', 'cmd');
if (tableCmd) {
  tableCmd.$('handles')(props.handles);
}

// 运行中的cmds, 避免重复运行
const runningCmds: Record<string, { running: boolean; time: number }> = {};

/**
 * 执行菜单的动作
 * @param menuId 菜单项ID
 */
async function handleSelect(menuId: string) {
  if (menuId.startsWith('@')) {
    menuId = menuId.substring(1);
  }

  const menuCfg =
    runningCmds[menuId] || (runningCmds[menuId] = { running: false, time: 0 });

  const now = new Date().valueOf();
  const time = menuCfg.time;
  menuCfg.time = now;

  // 不允许并行运行, 同时300ms内禁止运行
  if (menuCfg.running || now - time < 300) {
    return;
  }

  menuCfg.running = true;
  try {
    beaconHandle(menuId);
    await props.handles?.execute(menuId);
  } finally {
    menuCfg.running = false;
  }
}

function findMenuNodes(nodes: VNode[]) {
  if (nodes.length == 1 && _.isSymbol(nodes[0].type) && nodes[0].children) {
    return findMenuNodes(nodes[0].children as VNode[]);
  }

  return nodes || [];
}

// 找出所有子元素
const oldMenus = (() => {
  const slots = useSlots();
  const defaultSoltVNodes = slots.default?.() ?? [];
  const menus = findMenuNodes(defaultSoltVNodes);

  return _.sortBy(
    menus.filter((e) => !_.isSymbol(e.type)),

    // 排除, 默认隐藏的放在后面
    (e) => {
      if (
        e.props &&
        (e.props.defaultHide || _.isString(e.props['default-hide']))
      ) {
        // 默认不显示的
        return 1;
      }
      return 0;
    }
  );
})();

// 加载保存的布局
let layoutDatas = getLayout<{ id: string; show: boolean }[]>(
  'cmdbar',
  ['cmd', name],
  [],
  (v) => !!v.push
);

// 默认是否隐藏
function isMenuDefaultHide(menu: VNode) {
  if (menu.props?.defaultHide || _.isString(menu.props?.['default-hide'])) {
    return true;
  }
  return false;
}

// 合并布局
const menus = mergeLayout(layoutDatas, oldMenus, {
  id: (m) => m.props?.index,
  pinned: () => false,
  isShow: (m) => !isMenuDefaultHide(m),
  setShow: _.noop
});

function menuVisible(index: string) {
  let p = _.find(layoutDatas, (x) => x.id == index);
  if (!p) return true;
  return p.show;
}

function vnodesToComp(vnodes: VNode[]) {
  if (vnodes.length == 0) {
    return [];
  }

  return vnodes
    .filter((v) => hasRight(v))
    .map((v) => ({
      index: v.props?.index,
      label: v.props?.label,
      info: v.props?.info,
      comp: () => v,
      show: menuVisible(v.props!.index)
    }));
}

// 判断CmdMenu是否有使用权限
function hasRight(cmdMenu: VNode) {
  if (!cmdMenu.children || cmdMenu.children.length == 0) {
    return canAccess(cmdMenu.props?.require);
  }

  return hasAccessableChildren(cmdMenu.children as VNode[]);
}

// 判断是否有可访问的(有权限的)子菜单
function hasAccessableChildren(children: any) {
  if (!children) return false;

  if (_.isObject(children) && (children as any).default) {
    return hasAccessableChildren((children as any).default() as VNode[]);
  }

  if (!children.length) return false;

  for (const node of children) {
    if (!_.isObject(node.type)) continue;

    if (node.children) {
      if (hasAccessableChildren(node.children as VNode[])) {
        return true;
      }
    } else if (node.props && canAccess(node.props.require as string)) {
      return true;
    }
  }

  return false;
}

const defaultSlot = ref(vnodesToComp(menus));

const cmdbarRef = ref();
// shareData
shareData('cmdbar', {
  // 一级button调用
  select: async (menuId: string) => {
    await handleSelect(menuId);
  },

  // visible通知, 由CmdMenu注册上来
  //visible: menuShowNotify,

  // 提供给配置显示用
  setting: {
    cmdLabelMap: () => {
      return (
        defaultSlot.value.map((e) => ({
          id: e.index,
          label: e.label,
          info: e.info,
          show: e.show
        })) ?? []
      );
    },

    setVisible: (menuId: string, visible: boolean, save = true) => {
      if (menuId) {
        const p = _.find(layoutDatas, (e) => e.id == menuId);
        p!.show = visible;

        let comp = defaultSlot.value.find((e) => e.index == menuId);
        comp!.show = visible;
      }

      if (save) {
        // 保存配置
        saveLayout('cmdbar', ['cmd', name], layoutDatas);
      }
    },

    // 移动菜单
    moveCmd: (menuId: string, index: number) => {
      const oldIndex = _.findIndex(layoutDatas, (e) => e.id == menuId);

      arrayMove(layoutDatas, oldIndex, index);
      arrayMove(defaultSlot.value, oldIndex, index);

      // 保存配置
      saveLayout('cmdbar', ['cmd', name], layoutDatas);
    },

    // 恢复原始设置
    restore: () => {
      deleteLayout('cmdbar', ['cmd', name]);

      layoutDatas = oldMenus.map((e) => ({
        id: e.props?.index,
        show: !isMenuDefaultHide(e)
      }));

      defaultSlot.value = vnodesToComp(oldMenus);
    }
  }
});

const onMouseEnter = function () {
  document.body.style.setProperty('--st-sm-popper-offset', '0px');
};

// 处理overflow
// 从slots的vnode构造“更多操作”菜单
/*
<el-menu-item index="test1">测试1</el-menu-item>
<el-sub-menu index="group1" class="st-sub-menu" :popper-offset="0">
	<template #title>测试2</template>
	<el-menu-item index="test2">测试22</el-menu-item>
	<el-menu-item index="test3">测试33</el-menu-item>
</el-sub-menu>
*/
function buildMoreMenuItems(vnodes: VNode[]): VNode[] {
  let cmds: VNode[] = [];
  for (let v of vnodes) {
    let cmd = buildMoreMenuItem(v);
    if (cmd === undefined) {
      continue;
    }

    cmds.push(cmd);
  }

  return cmds;
}

function buildMoreMenuItem(vnode: VNode) {
  const index = '@' + vnode.props?.index; // 前面加一个@因为index不能重复
  const label = vnode.props?.label;

  let cmdVnode: VNode;

  if (vnode.type === CmdMenu) {
    // 一级菜单
    if (!vnode.children) {
      cmdVnode = h(
        ElMenuItem,
        {
          index,
          label
        },
        () => label
      );
    } else {
      cmdVnode = h(
        ElSubMenu,
        {
          index,
          label,
          popperOffset: 0,
          class: 'st-sub-menu'
        },
        {
          title: () => label,
          default: () =>
            buildMoreMenuItems((vnode.children as any).default?.() ?? [])
        }
      );
    }
  } else if (vnode.type === CmdSubMenu) {
    cmdVnode = h(
      ElSubMenu,
      {
        index,
        label,
        popperOffset: 0,
        class: 'st-sub-menu'
      },
      {
        title: () => label,
        default: () =>
          buildMoreMenuItems((vnode.children as any).default?.() ?? [])
      }
    );
  } else if (vnode.type === CmdMenuItem) {
    cmdVnode = h(
      ElMenuItem,
      {
        index,
        label
      },
      () => label
    );
  } else {
    return undefined;
  }

  return cmdVnode;
}

// more菜单
const moreMenuRef = ref();

const menusRef = ref([]);
// 隐藏的菜单
const hiddenMenuMap = reactive<Record<string, VNode>>({});
const menuMap = new Map<HTMLElement, { name: string; el: HTMLElement }>();

let observer: IntersectionObserver;

function getMenuItemEl(instance: HTMLElement) {
  // 此处不明,使用component会多一个text节点
  if (instance.nodeName == '#text')
    return getMenuItemEl(instance.nextElementSibling! as HTMLElement);
  return instance;
}

// more menu的位置
const { left: cmdbarLeft } = useElementBounding(cmdbarRef);

// 最后个可见菜单menu
const lastVisibleMenu = computed(() => {
  let last: HTMLElement | undefined = undefined;
  for (let m of menusRef.value) {
    const el = getMenuItemEl(m);
    if (!el.style) continue;

    const name = el.style.getPropertyValue('--cmd-name') || '';
    if (hiddenMenuMap[name]) {
      break;
    }

    last = el;
  }

  return last;
});

const { right: lastVisibleMenuRight } = useElementBounding(lastVisibleMenu);

// more menu水平移动距离
const moreMenuLeft = computed(() => {
  if (
    !lastVisibleMenu.value ||
    !lastVisibleMenuRight.value ||
    !cmdbarLeft.value
  ) {
    return '0px';
  }

  return lastVisibleMenuRight.value - cmdbarLeft.value + 40 + 'px';
});

watch(cmdbarRef, (cmdbar) => {
  if (observer) {
    observer.disconnect();
  }

  observer = new IntersectionObserver(
    (changes) => {
      changes.forEach((change) => {
        const { intersectionRatio, target } = change;

        const name =
          (target as HTMLElement).style.getPropertyValue('--cmd-name') || '';

        if (intersectionRatio < 1) {
          if (!hiddenMenuMap[name]) {
            hiddenMenuMap[name] = buildMoreMenuItem(
              oldMenus.find((e) => e.props?.index == name)!
            )!;
          }
        } else {
          Reflect.deleteProperty(hiddenMenuMap, name);
        }
      });
    },
    {
      root: cmdbar.$el,
      threshold: [0, 0.5, 1]
    }
  );
});

watch(
  menusRef,
  (menus) => {
    // 检查新增的tab
    for (const menu of menus) {
      if (menuMap.has(menu)) {
        continue;
      }

      const el = getMenuItemEl(menu);
      if (!el.style) continue;

      const name = el?.style.getPropertyValue('--cmd-name') || '';
      if (name) {
        observer.observe(el);
        menuMap.set(menu, { name, el });
      }
    }

    // 以下检查被销毁的menu
    const deleting: HTMLElement[] = [];
    for (const name of menuMap.keys()) {
      if (!menus.find((m) => m == name)) {
        deleting.push(name);
      }
    }

    // 处理销毁的menu
    deleting.forEach((m) => {
      const { name, el } = menuMap.get(m)!;

      observer.unobserve(el);
      menuMap.delete(m);

      Reflect.deleteProperty(hiddenMenuMap, name);
    });
  },
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<style lang="scss" scoped>
.cb-cont {
  position: relative;
  height: 62px;
  padding: 0 12px;
  margin: 0px var(--easy-page-padding-x);
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid var(--easy-cmdbar-border-color);
  border-bottom: none;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  user-select: none;

  :deep(.el-button.is-link) {
    color: #606266;
    border: none;
    padding: 0 8px;
    background-color: transparent;
  }

  :deep(.el-button.is-link:hover) {
    color: #606266;
    background-color: #f4f6f9;
  }

  :deep(.el-button.is-link.is-disabled) {
    color: #a8abb2;
  }
}

.easy-popup .sp-cont .cb-cont {
  margin: 0 12px;
}

.left {
  display: flex;
  column-gap: 8px;
  padding-right: 8px;
}

.right {
  display: flex;
  column-gap: 8px;

  --sf-font-size: 14px;
  --sf-line-height: 26px;
  --sf-prefix-left-space: 4px;
  --sf-prefix-right-space: 6px;
  --sf-border-color: #e9e9ea;
  --sf-border-hover-color: #e9e9ea;
  --sf-border-radius: 2px;

  :deep(> .sf) {
    max-width: 180px;
  }
}

.cmdbar {
  flex: 1;
  overflow: hidden;
  border-bottom: none;
  height: 100%;
  align-items: center;

  --el-menu-active-color: #666;

  :deep(.el-menu-item) {
    box-sizing: border-box;
    height: 28px;
    min-width: 50px;
    margin-right: 8px;
    box-shadow: none;
    border-radius: 2px;
    border: 1px solid #bbbfc4;
    //color: #192865;
  }

  :deep(.el-menu-item:hover) {
    border-color: #2f7ff4;
    color: #2f7ff4 !important;
  }

  //:deep(.el-menu-item:active)
  //{
  //background-color: #2F7FF4!important;
  //color: white!important;;
  //}

  :deep(.el-menu-item.is-active) {
    border-bottom: 1px solid #bbbfc4;
    color: #192865;
  }

  :deep(.el-menu-item.is-active:hover) {
    border-bottom: 1px solid #2f7ff4;
    color: #2f7ff4;
  }

  //:deep(.el-menu-item.is-active:active)
  //{
  //background-color: #2F7FF4;
  //color: white;
  //}

  :deep(.el-menu-item[primary]) {
    border: 1px solid #327de8;
    background-color: #327de8 !important;
    color: white !important;
  }

  :deep(.el-menu-item[primary]:hover) {
    border: 1px solid #5b97ed;
    background-color: #5b97ed !important;
    color: white !important;
  }
}

.more-menu {
  border: none;
  position: absolute;
  left: v-bind(moreMenuLeft);
}

.more-op {
  height: 28px;

  :deep(.el-sub-menu__title) {
    border: none !important;
    color: #2f7ff4;
    padding: 0 8px;
  }

  :deep(.el-sub-menu__title:hover) {
    background-color: #eaf2fd;
  }

  :deep(.el-sub-menu__icon-arrow) {
    display: none;
  }
}

.more-op.is-opened :deep(.el-sub-menu__title) {
  background-color: #eaf2fd;
}

.cmd-space {
  width: calc(v-bind(reserveSpace) + 90px);
  height: 100%;
}
</style>
